import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const BRAND = 'More Profit Per Call';
const BRAND_COLOR = [59, 130, 246]; // #3B82F6
const DARK = [17, 24, 39];
const GRAY = [107, 114, 128];
const LIGHT_BG = [245, 247, 250];
const PAGE_MARGIN = 16;
const HEADER_H = 36;
const CONTENT_START = HEADER_H + 10;

function addHeader(doc, report) {
  const pw = doc.internal.pageSize.width;

  doc.setFillColor(...BRAND_COLOR);
  doc.rect(0, 0, pw, HEADER_H, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(BRAND, PAGE_MARGIN, 16);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Call Coaching Report', PAGE_MARGIN, 24);

  const dateStr = new Date(report.startedAt).toLocaleString();
  doc.text(dateStr, pw - PAGE_MARGIN, 16, { align: 'right' });

  const duration = report.duration ? `Duration: ${Math.round(report.duration / 1000)}s` : '';
  const playbook = `Playbook: ${report.playbook || 'general'}`;
  const langParts = [duration, playbook];
  if (report.inputLanguage && report.inputLanguage !== 'en') langParts.push(`STT: ${report.inputLanguage.toUpperCase()}`);
  if (report.outputLanguage && report.outputLanguage !== 'en') langParts.push(`Output: ${report.outputLanguage.toUpperCase()}`);
  doc.text(langParts.filter(Boolean).join('  |  '), pw - PAGE_MARGIN, 24, { align: 'right' });
}

function addFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text(
      `${BRAND}  •  Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
  }
}

function checkPage(doc, report, y, needed = 20) {
  if (y + needed > doc.internal.pageSize.height - 20) {
    doc.addPage();
    addHeader(doc, report);
    return CONTENT_START;
  }
  return y;
}

function sectionTitle(doc, text, y) {
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(text, PAGE_MARGIN, y);
  return y + 8;
}

function addListSection(doc, report, title, items, y, color) {
  const pw = doc.internal.pageSize.width;
  y = checkPage(doc, report, y, 20);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(title, PAGE_MARGIN, y);
  y += 6;

  for (const item of items) {
    y = checkPage(doc, report, y, 10);
    doc.setFillColor(...color);
    doc.circle(PAGE_MARGIN + 3, y - 1.2, 1.2, 'F');
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    const lines = doc.splitTextToSize(String(item), pw - PAGE_MARGIN * 2 - 12);
    doc.text(lines, PAGE_MARGIN + 8, y);
    y += lines.length * 4.5 + 3;
  }
  return y + 4;
}

export default function generatePDF(report) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = doc.internal.pageSize.width;
  let y = CONTENT_START;

  addHeader(doc, report);

  const { summary, transcript, suggestions, customerIntel, researchItems, stats, talkRatio } = report;

  // ── Summary ────────────────────────────────────────────────
  if (summary) {
    y = sectionTitle(doc, 'Summary', y);

    if (summary.score != null) {
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BRAND_COLOR);
      doc.text(`${summary.score}/10`, PAGE_MARGIN, y + 4);

      if (summary.verdict) {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...DARK);
        const verdictLines = doc.splitTextToSize(summary.verdict, pw - PAGE_MARGIN - 52);
        doc.text(verdictLines, 40, y - 1);
        y += Math.max(14, verdictLines.length * 5 + 6);
      } else {
        y += 14;
      }
    } else if (summary.verdict) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DARK);
      const lines = doc.splitTextToSize(summary.verdict, pw - PAGE_MARGIN * 2);
      doc.text(lines, PAGE_MARGIN, y);
      y += lines.length * 5 + 6;
    }

    if (summary.went_well?.length) {
      y = addListSection(doc, report, 'What Went Well', summary.went_well, y, [16, 185, 129]);
    }
    if (summary.improvements?.length) {
      y = addListSection(doc, report, 'Areas for Improvement', summary.improvements, y, [245, 158, 11]);
    }

    if (summary.key_moments?.length) {
      y = checkPage(doc, report, y, 20);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...DARK);
      doc.text('Key Moments', PAGE_MARGIN, y);
      y += 6;

      for (const m of summary.key_moments) {
        y = checkPage(doc, report, y, 14);
        const ts = m.timestamp_label || m.timestamp || m.time || '';
        const desc = m.description || m.text || m.moment || '';

        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...GRAY);
        doc.text(String(ts), PAGE_MARGIN + 2, y);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...DARK);
        const descLines = doc.splitTextToSize(String(desc), pw - PAGE_MARGIN * 2 - 8);
        doc.text(descLines, PAGE_MARGIN + 2, y + 4.5);
        y += 5 + descLines.length * 4.5 + 3;
      }
      y += 2;
    }

    if (summary.coaching_effectiveness) {
      y = checkPage(doc, report, y, 14);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...GRAY);
      const ce = summary.coaching_effectiveness;
      doc.text(`Coaching: ${ce.suggestions_given ?? '—'} given, ${ce.suggestions_accepted ?? '—'} accepted`, PAGE_MARGIN, y);
      y += 5;
      if (ce.assessment) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...DARK);
        const aLines = doc.splitTextToSize(String(ce.assessment), pw - PAGE_MARGIN * 2);
        doc.text(aLines, PAGE_MARGIN, y);
        y += aLines.length * 4 + 4;
      }
    }

    if (summary.customer_sentiment) {
      y = checkPage(doc, report, y, 10);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...GRAY);
      doc.text('Customer Sentiment:', PAGE_MARGIN, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DARK);
      doc.text(String(summary.customer_sentiment), PAGE_MARGIN + 40, y);
      y += 7;
    }
    if (summary.next_steps) {
      y = checkPage(doc, report, y, 10);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...GRAY);
      doc.text('Next Steps:', PAGE_MARGIN, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DARK);
      const nsLines = doc.splitTextToSize(String(summary.next_steps), pw - PAGE_MARGIN - 42);
      doc.text(nsLines, PAGE_MARGIN + 24, y);
      y += nsLines.length * 4.5 + 6;
    }
  }

  // ── Statistics ──────────────────────────────────────────────
  if (stats || talkRatio || report.platformCost) {
    y = checkPage(doc, report, y, 30);
    y += 2;
    y = sectionTitle(doc, 'Statistics', y);

    const statRows = [];
    if (stats) {
      statRows.push(['Transcript Lines', String(stats.transcriptCount || 0)]);
      statRows.push(['Suggestions', String(stats.suggestionCount || 0)]);
      statRows.push(['Accepted', String(stats.suggestionsAccepted || 0)]);
      statRows.push(['Dismissed', String(stats.suggestionsDismissed || 0)]);
    }
    if (talkRatio) {
      statRows.push(['Agent Talk %', `${Math.round((talkRatio.ratio || 0) * 100)}%`]);
    }
    if (report.platformCost) {
      const pc = report.platformCost;
      statRows.push(['Duration', `${Math.floor(pc.durationSeconds / 60)}m ${pc.durationSeconds % 60}s`]);
      statRows.push(['Rate', `$${pc.ratePerMinute?.toFixed(2)}/min`]);
      statRows.push(['Platform Fee', `$${pc.dollars}`]);
    }

    autoTable(doc, {
      startY: y,
      head: [],
      body: statRows,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 }, textColor: DARK },
      columnStyles: {
        0: { fontStyle: 'bold', textColor: GRAY, cellWidth: 48 },
        1: { textColor: DARK },
      },
      margin: { left: PAGE_MARGIN },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  // ── Customer Intelligence ──────────────────────────────────
  if (customerIntel?.length) {
    y = checkPage(doc, report, y, 24);
    y = sectionTitle(doc, 'Customer Intelligence', y);

    const typeBadgeColors = { info: [59, 130, 246], concern: [245, 158, 11], request: [16, 185, 129], objection: [239, 68, 68] };

    for (const item of customerIntel) {
      y = checkPage(doc, report, y, 10);
      const color = typeBadgeColors[item.type] || typeBadgeColors.info;
      doc.setFillColor(...color);
      doc.roundedRect(PAGE_MARGIN, y - 3.5, 23, 5.5, 1.2, 1.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text((item.type || 'info').toUpperCase(), PAGE_MARGIN + 1.5, y);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DARK);
      const textLines = doc.splitTextToSize(String(item.text || ''), pw - PAGE_MARGIN - 46);
      doc.text(textLines, PAGE_MARGIN + 26, y);
      y += textLines.length * 4.5 + 3;
    }
    y += 6;
  }

  // ── Research Notes ─────────────────────────────────────────
  if (researchItems?.length) {
    y = checkPage(doc, report, y, 24);
    y = sectionTitle(doc, 'Research Notes', y);

    const researchBadgeColors = {
      company: [30, 64, 175], product: [6, 95, 70], regulation: [153, 27, 27],
      term: [55, 48, 163], person: [146, 64, 14], topic: [107, 33, 168],
    };

    for (const item of researchItems) {
      y = checkPage(doc, report, y, 16);
      const color = researchBadgeColors[item.type] || researchBadgeColors.topic;
      doc.setFillColor(...color);
      doc.roundedRect(PAGE_MARGIN, y - 3.5, 24, 5.5, 1.2, 1.2, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text((item.type || 'topic').toUpperCase(), PAGE_MARGIN + 1.5, y);

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...DARK);
      doc.text(String(item.subject || ''), PAGE_MARGIN + 27, y);
      y += 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(75, 85, 99);
      const insightLines = doc.splitTextToSize(String(item.insight || ''), pw - PAGE_MARGIN - 4);
      doc.text(insightLines, PAGE_MARGIN + 2, y);
      y += insightLines.length * 4.5 + 4;
    }
    y += 6;
  }

  // ── Coaching Suggestions ───────────────────────────────────
  if (suggestions?.length) {
    y = checkPage(doc, report, y, 24);
    y = sectionTitle(doc, 'Coaching Suggestions', y);

    const urgencyColors = { info: [59, 130, 246], warning: [245, 158, 11], critical: [239, 68, 68] };

    for (const s of suggestions) {
      y = checkPage(doc, report, y, 16);
      const color = urgencyColors[s.urgency] || urgencyColors.info;
      doc.setDrawColor(...color);
      doc.setLineWidth(0.8);
      doc.line(PAGE_MARGIN, y - 2, PAGE_MARGIN, y + 8);

      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...color);
      let label = (s.urgency || 'info').toUpperCase();
      if (s.action) label += `  [${s.action}]`;
      doc.text(label, PAGE_MARGIN + 3, y);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DARK);
      const sLines = doc.splitTextToSize(s.text || '', pw - PAGE_MARGIN * 2 - 8);
      doc.text(sLines, PAGE_MARGIN + 3, y + 5);
      y += 5 + sLines.length * 4.5 + 5;
    }
    y += 4;
  }

  // ── Transcript ─────────────────────────────────────────────
  if (report.transcriptHidden) {
    doc.addPage();
    addHeader(doc, report);
    y = CONTENT_START;
    y = sectionTitle(doc, 'Full Transcript', y);
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    doc.text('Transcript not saved \u2014 Compliance Mode was enabled.', MARGIN, y + 6);
  } else if (transcript?.length) {
    doc.addPage();
    addHeader(doc, report);
    y = CONTENT_START;
    y = sectionTitle(doc, 'Full Transcript', y);

    const rows = transcript.map((e) => [
      e.role === 'agent' ? 'Agent' : 'Customer',
      new Date(e.timestamp).toLocaleTimeString(),
      e.text,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Speaker', 'Time', 'Text']],
      body: rows,
      theme: 'striped',
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
        textColor: DARK,
        overflow: 'linebreak',
        lineWidth: 0,
      },
      headStyles: {
        fillColor: BRAND_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
      },
      alternateRowStyles: { fillColor: LIGHT_BG },
      columnStyles: {
        0: { cellWidth: 24, fontStyle: 'bold' },
        1: { cellWidth: 24, textColor: GRAY },
        2: { cellWidth: 'auto' },
      },
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN, top: CONTENT_START },
      didDrawPage: (data) => {
        addHeader(doc, report);
        if (data.pageNumber > 1) {
          data.settings.startY = CONTENT_START;
        }
      },
    });
  }

  addFooter(doc);

  doc.save(`call-report-${report.callSid || 'unknown'}.pdf`);
}
